import {
  SECURITY_TYPE_OBJ,
  PRICE_SOURCE,
  PRICE_SOURCE_LABELS,
  PRICE_SOURCE_OBJ,
  PRICE_SOURCE_VARIANTS,
  type TSecurityType,
} from "./portfolio.constants";
import {
  BROKER_SYNC_PROVIDER_LABELS,
  BROKER_SYNC_PROVIDER_OBJ,
  BROKER_SYNC_PROVIDER_VARIANTS,
} from "./broker-sync.constants";
import type { ChipVariant } from "../types/smart-table";

/** Portfolio identities are separate from the Symbol security catalogue. */
export const PORTFOLIO_INSTRUMENT_KIND = ["security", "option"] as const;
export type TPortfolioInstrumentKind = (typeof PORTFOLIO_INSTRUMENT_KIND)[number];
export const PORTFOLIO_INSTRUMENT_KIND_OBJ = {
  SECURITY: "security", OPTION: "option",
} as const satisfies Record<string, TPortfolioInstrumentKind>;
export const PORTFOLIO_INSTRUMENT_KIND_LABELS: Record<TPortfolioInstrumentKind, Record<string, string>> = {
  security: { "en-US": "Security", "ru-RU": "Ценная бумага" },
  option: { "en-US": "Option", "ru-RU": "Опцион" },
};
export const PORTFOLIO_INSTRUMENT_KIND_VARIANTS: Record<TPortfolioInstrumentKind, ChipVariant> = {
  security: "default", option: "secondary",
};
export const PORTFOLIO_DERIVATIVE_INSTRUMENT_KIND = [PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION] as const;
export const PORTFOLIO_OPTION_MANUAL_WRITE_MESSAGE = "Опционные сделки загружаются из IBKR и доступны только для просмотра. Для отмены используйте откат синхронизации брокера.";

export const PORTFOLIO_INSTRUMENT_PRICE_SOURCE = [...PRICE_SOURCE, BROKER_SYNC_PROVIDER_OBJ.IBKR] as const;
export type TPortfolioInstrumentPriceSource = (typeof PORTFOLIO_INSTRUMENT_PRICE_SOURCE)[number];
export const PORTFOLIO_INSTRUMENT_PRICE_SOURCE_OBJ = { ...PRICE_SOURCE_OBJ, IBKR: BROKER_SYNC_PROVIDER_OBJ.IBKR } as const;
export const PORTFOLIO_INSTRUMENT_PRICE_SOURCE_LABELS = { ...PRICE_SOURCE_LABELS, [BROKER_SYNC_PROVIDER_OBJ.IBKR]: BROKER_SYNC_PROVIDER_LABELS[BROKER_SYNC_PROVIDER_OBJ.IBKR] };
export const PORTFOLIO_INSTRUMENT_PRICE_SOURCE_VARIANTS = { ...PRICE_SOURCE_VARIANTS, [BROKER_SYNC_PROVIDER_OBJ.IBKR]: BROKER_SYNC_PROVIDER_VARIANTS[BROKER_SYNC_PROVIDER_OBJ.IBKR] };

/** Only IBKR options are supported; reuse its existing broker identity. */
export const OPTION_CONTRACT_PROVIDER = [BROKER_SYNC_PROVIDER_OBJ.IBKR] as const;
export type TOptionContractProvider = (typeof OPTION_CONTRACT_PROVIDER)[number];
export const OPTION_CONTRACT_PROVIDER_OBJ = { IBKR: BROKER_SYNC_PROVIDER_OBJ.IBKR } as const;
export const OPTION_CONTRACT_PROVIDER_LABELS: Record<TOptionContractProvider, Record<string, string>> = {
  [BROKER_SYNC_PROVIDER_OBJ.IBKR]: BROKER_SYNC_PROVIDER_LABELS[BROKER_SYNC_PROVIDER_OBJ.IBKR],
};
export const OPTION_CONTRACT_PROVIDER_VARIANTS: Record<TOptionContractProvider, ChipVariant> = {
  [BROKER_SYNC_PROVIDER_OBJ.IBKR]: BROKER_SYNC_PROVIDER_VARIANTS[BROKER_SYNC_PROVIDER_OBJ.IBKR],
};

export const OPTION_RIGHT = ["call", "put"] as const;
export type TOptionRight = (typeof OPTION_RIGHT)[number];
export const OPTION_RIGHT_OBJ = { CALL: "call", PUT: "put" } as const satisfies Record<string, TOptionRight>;
export const OPTION_RIGHT_LABELS: Record<TOptionRight, Record<string, string>> = {
  call: { "en-US": "Call", "ru-RU": "Call" },
  put: { "en-US": "Put", "ru-RU": "Put" },
};
export const OPTION_RIGHT_VARIANTS: Record<TOptionRight, ChipVariant> = {
  call: "default", put: "secondary",
};

/** Confirmed long-option events. Buys and closing sells use TRANSACTION_TYPE. */
export const OPTION_EVENT_KIND = ["expiration", "exercise", "cash-settlement"] as const;
export type TOptionEventKind = (typeof OPTION_EVENT_KIND)[number];
export const OPTION_EVENT_KIND_OBJ = {
  EXPIRATION: "expiration", EXERCISE: "exercise", CASH_SETTLEMENT: "cash-settlement",
} as const satisfies Record<string, TOptionEventKind>;
export const OPTION_EVENT_KIND_LABELS: Record<TOptionEventKind, Record<string, string>> = {
  expiration: { "en-US": "Confirmed expiration", "ru-RU": "Подтверждённое истечение" },
  exercise: { "en-US": "Confirmed exercise", "ru-RU": "Подтверждённое исполнение" },
  "cash-settlement": { "en-US": "Confirmed cash settlement", "ru-RU": "Подтверждённый денежный расчёт" },
};
export const OPTION_EVENT_KIND_VARIANTS: Record<TOptionEventKind, ChipVariant> = {
  expiration: "outline", exercise: "secondary", "cash-settlement": "default",
};
export const OPTION_PHYSICAL_EVENT_KIND = [OPTION_EVENT_KIND_OBJ.EXERCISE] as const;
export const OPTION_NON_DELIVERY_EVENT_KIND = [
  OPTION_EVENT_KIND_OBJ.EXPIRATION, OPTION_EVENT_KIND_OBJ.CASH_SETTLEMENT,
] as const;

/** Allocation classes reuse the security vocabulary without adding options to Symbol. */
export const PORTFOLIO_ASSET_CLASS = [
  SECURITY_TYPE_OBJ.STOCK, SECURITY_TYPE_OBJ.BOND, SECURITY_TYPE_OBJ.FUND,
  SECURITY_TYPE_OBJ.WARRANT,
  PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION, "cash", "other",
] as const;
export type TPortfolioAssetClass = (typeof PORTFOLIO_ASSET_CLASS)[number];
export const PORTFOLIO_ASSET_CLASS_OBJ = {
  STOCK: SECURITY_TYPE_OBJ.STOCK,
  BOND: SECURITY_TYPE_OBJ.BOND,
  FUND: SECURITY_TYPE_OBJ.FUND,
  WARRANT: SECURITY_TYPE_OBJ.WARRANT,
  OPTION: PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION,
  CASH: "cash",
  OTHER: "other",
} as const satisfies Record<string, TPortfolioAssetClass>;
export const PORTFOLIO_ASSET_CLASS_LABELS: Record<TPortfolioAssetClass, Record<string, string>> = {
  stock: { "en-US": "Stocks", "ru-RU": "Акции" },
  bond: { "en-US": "Bonds", "ru-RU": "Облигации" },
  fund: { "en-US": "Funds", "ru-RU": "Фонды" },
  warrant: { "en-US": "Warrants", "ru-RU": "Варранты" },
  option: { "en-US": "Options", "ru-RU": "Опционы" },
  cash: { "en-US": "Cash", "ru-RU": "Деньги" },
  other: { "en-US": "Other", "ru-RU": "Прочее" },
};
export const PORTFOLIO_ASSET_CLASS_VARIANTS: Record<TPortfolioAssetClass, ChipVariant> = {
  stock: "default", bond: "secondary", fund: "outline", warrant: "outline", option: "secondary", cash: "emerald", other: "outline",
};
export const PORTFOLIO_SECURITY_ASSET_CLASS = [
  PORTFOLIO_ASSET_CLASS_OBJ.STOCK, PORTFOLIO_ASSET_CLASS_OBJ.BOND, PORTFOLIO_ASSET_CLASS_OBJ.FUND,
  PORTFOLIO_ASSET_CLASS_OBJ.WARRANT,
] as const;
/** Currency instruments contribute cash movements; they do not create investment lots. */
export const PORTFOLIO_SECURITY_ASSET_CLASS_BY_TYPE = {
  [SECURITY_TYPE_OBJ.STOCK]: PORTFOLIO_ASSET_CLASS_OBJ.STOCK,
  [SECURITY_TYPE_OBJ.PREFERRED_STOCK]: PORTFOLIO_ASSET_CLASS_OBJ.STOCK,
  [SECURITY_TYPE_OBJ.ADR]: PORTFOLIO_ASSET_CLASS_OBJ.STOCK,
  [SECURITY_TYPE_OBJ.BOND]: PORTFOLIO_ASSET_CLASS_OBJ.BOND,
  [SECURITY_TYPE_OBJ.FUND]: PORTFOLIO_ASSET_CLASS_OBJ.FUND,
  [SECURITY_TYPE_OBJ.CURRENCY]: PORTFOLIO_ASSET_CLASS_OBJ.CASH,
  [SECURITY_TYPE_OBJ.WARRANT]: PORTFOLIO_ASSET_CLASS_OBJ.WARRANT,
} as const satisfies Record<TSecurityType, TPortfolioAssetClass>;
